import React from "react";
import { applyFilter } from "./interpolation-filter";
import { getExternalID } from "../lib/entityIds";
import { captureMessage, linkifyPlexIds, stylizePublications } from "../lib/plexText";
import { deriveTemplate, TEMPLATE_CONSTANTS } from "./index";
import { unescapeMD } from "./utils";
import { toJS } from "mobx";
import { DEV, BETA } from "../lib/configMgr";

// Needs to handle entity properties and potential component properties injected into the entity
// { name1 name2 ... | filter1 | filter2 ... }
export const RE_PROPERTY_EXPR = /^_?[a-z][-:\w]+(\s+[a-z][-:\w]+)*(\s*\|\s*\S+.*)?$/;

export const IPROP_PREFIX = "_interpolated_";

const RE_INTERPOLATION_ENTIRE = /^\{\s*[a-z][-:\w]+[^}]*}$/;
const RE_INTERPOLATION = /\{\s*((?!_interpolated_)[_a-z][-:\w]+[^}]*)}/g;
const RE_JSX_COMPONENT = /<([A-Z][a-zA-Z0-9]+)([^\/>]+)(\/?>)/g;
const RE_JSX_ATTRIBUTE = /(?<=\s)([a-z][a-zA-Z0-9]+)=\{([^}]+)}/g;

const hasValue = (x) => !!x || x === false || x === 0;

const composeInterpolation = (keys, filters, props = {}, interpolatedProps = {}, uniqueValues,
                              overrides = {}, markText = null, quote = x => x) => {
  // Compose a single interpolation between curly braces
  const NOVALUE = "";
  const [propName, propValue] = keys.reduce((result, name) => {
    if (hasValue(result[1])) {
      return result;
    }
    return [name, props[name]];
  }, [null, null]);
  if (hasValue(propValue) || filters.some(([name, ...rest]) => name === "default")) {
    // Filters may result in markup, so quote before filtering
    const filtered = filters.reduce((result, [filter, filterArg]) => {
      if (result === NOVALUE && filter !== "default") {
        return NOVALUE;
      }
      try {
        return applyFilter(result, filter, filterArg, uniqueValues, overrides,
                           interpolatedProps, propName, markText, quote);
      } catch (e) {
        console.error(`Could not apply filter '${filter}' to '${result}'`, e);
        return result;
      }
    }, propValue);
    if (hasValue(filtered)) {
      if (typeof(propValue) === 'string' && uniqueValues) {
        uniqueValues.add(propValue.toLowerCase());
        if (props.id === `smiles:${propValue}` || props.id === `query:${propValue}`) {
          uniqueValues.add(props.id.toLowerCase());
        }
      }
      return filtered;
    }
  }
  return NOVALUE;
};

export const interpolate = (template,
                            entity,
                            componentProps = {},
                            markText = null,
                            uniqueValues = new Set(),
                            overrides = {},
                            quote = x => x) => {
  if (typeof (template) !== 'string') {
    return template;
  }
  if (RE_INTERPOLATION_ENTIRE.test(template || "")) {
    const interpolated = evaluateInterpolationExpression(template.substring(1, template.length - 1), entity,
                                                          componentProps, uniqueValues, overrides,
                                                          markText, quote);
    // If interpolation failed (returned false), return empty string instead of "false"
    if (interpolated === false) {
      return "";
    }
    const value = "" + interpolated;
    return markText ? markText(value) : value;
  }

  // Account for multiple interpolations within a property, e.g. title='{foo} {bar}'
  // markdown-to-jsx interpolates '{EXP}' expressions as either string or boolean
  // Handle HTML/JSX element attributes "foo={bar}"; the expression is evaluated and the property value injected
  // into componentProps.  A property placeholder is inserted for later resolution to avoid property name conflicts
  // and allow properties to have JSX values (e.g. markText).
  // JSX components must start with a capital letter.
  template = template.replace(RE_JSX_COMPONENT, (match, componentName, attributes, closure) => {
    const overrideProps = overrides[componentName]?.props || {}
    const attrs = attributes.replace(RE_JSX_ATTRIBUTE, (match, attributeName, attributeValue) => {
      const value = evaluateInterpolationExpression(attributeValue, entity, {...componentProps, ...overrideProps},
                                                    uniqueValues, overrides);
      const propName = substituteAttributeValue(attributeName, value, componentProps, markText);
      delete overrideProps[attributeName];
      return `${attributeName}={${propName}}`;
    });
    return `<${componentName}${attrs}${closure}`;
  });
  // Handle "{bar}" in freeform text
  let idx = 0;
  return template.replace(RE_INTERPOLATION, (match, expr) => {
    const interpolated = evaluateInterpolationExpression(expr, entity, componentProps,
                                                         uniqueValues, overrides,
                                                         markText, quote);
    const value = interpolated === false ? match : interpolated;
    // Assume any markdown is already properly quoted
    if (typeof (value) !== "string" || (value.startsWith("<") && value.endsWith(">"))) {
      return "" + value;
    }
    // Clean up any literal HTML br tags from source data first, then normalize newlines
    const trimmed = value
      .replace(/<br[^>]*>/gi, "\n")
      .replace(/(\s*\n\s*)+/g, "\n")
      .replace(/\n+/g, "<br class='newline' />");

    const marked = markText && markText(trimmed);
    if (marked) {
      return createInterpolatedProperty(marked, componentProps);
    }
    // If trimmed contains HTML tags (like <br>), wrap in span to prevent escaping
    if (trimmed.includes("<br")) {
      return `<span>${trimmed}</span>`;
    }
    return quote(trimmed);
  });
};

export const substituteAttributeValue = (attributeName, value, componentProps, markText = null) => {
  // Replace the attribute with an interpolated attribute, which may be an object; this allows
  // substitution of the object into component property lists by using a property name placeholder in
  // a string passed to the markdown renderer
  const propName = `${IPROP_PREFIX}${attributeName}_${Object.keys(componentProps).length}`;
  componentProps[propName] = markText && typeof (value) === "string"
                             ? markText(value)
                             : value;
  return propName;
}

const createInterpolatedProperty = (value, componentProps, key = null) => {
  const propName = `${IPROP_PREFIX}inline_${Object.keys(componentProps).length}`;
  componentProps[propName] = value;
  // Child rendering will replace the value of the property attribute
  // propName is already unique based on componentProps.length, so use it directly as the key
  return `<InterpolatedProperty property={${propName}} key="${propName}" />`;
};

const evaluateInterpolationExpression = (expr, entity, interpolatedProps = {}, uniqueValues,
                                         overrides = {}, markText = null, quote = x => x) => {
  if (expr === "__dump__") {
    return JSON.stringify(entity);
  }
  if (typeof (expr) === 'string' && RE_PROPERTY_EXPR.test(expr)) {
    const parts = expr.split('|').map(x => x.trim());
    const keys = parts[0].split(/\s+/).map(x => unescapeMD(x.trim()));
    const filters = parts.slice(1).map(x => x.split('=')
      .map(x => x.trim().replace(/^'(.*)'$/, '$1').replace(/^"(.*)"$/, '$1')));
    return composeInterpolation(keys, filters, {...interpolatedProps, ...entity}, interpolatedProps, uniqueValues,
                                overrides, markText, quote);
  }
  return false;
};

export const interpolateProps = (templateProps, entity, interpolatedProps = {}, uniqueValues = new Set(), markText = null, templateOverrides = {}) => {
  // Returns a dict with _only_ those properties which were interpolated
  // TODO: sort by dependency
  const newProps = {};
  // Properties that should not be highlighted by filter text
  const NO_MARK_PROPS = new Set(['rank', 'ordinal', 'total']);

  Object.keys(templateProps).forEach(k => {
    const value = templateProps[k];
    if (value && typeof(value) === 'string') {
      // Don't apply markText to rank, ordinal, or total - these are structural UI elements
      const shouldMark = markText && !NO_MARK_PROPS.has(k);
      const interpolated = interpolate(value, entity, interpolatedProps, shouldMark ? markText : null, uniqueValues, templateOverrides);
      if (interpolated !== value) {
        newProps[k] = interpolated;
      }
    }
  });
  return newProps;
};

export const resolvePropertyValue = (value, props = {}, visited = new Set(), depth = 0, context = null) => {
  // Interpolate the given property value, recursively
  // Protect against circular references and excessive recursion depth
  const MAX_DEPTH = 20;

  if (depth > MAX_DEPTH) {
    console.error(`Maximum interpolation depth (${MAX_DEPTH}) exceeded for value:`, value);
    captureMessage(`Interpolation depth limit exceeded`, "warning", {
      value,
      depth,
      entityId: props.id,
      entityCategory: props.category,
      context
    });
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(el => resolvePropertyValue(el, props, visited, depth + 1, context));
  }
  if (typeof(value) === "string") {
    if (value === "__dump__") {
      return JSON.stringify(props);
    }
    const interpolated = interpolate(value, props, {}, null, new Set(), {}, x => x);
    // Recurse if the interpolation resulted in a single property name that exists in props
    // and is different from the value we just interpolated (to avoid false circular refs)
    if (props[interpolated] && interpolated !== value) {
      // Check for circular reference
      if (visited.has(interpolated)) {
        console.error(`Circular property reference detected: ${Array.from(visited).join(' -> ')} -> ${interpolated}`);
        captureMessage(`Circular property reference detected`, "warning", {
          chain: Array.from(visited).concat(interpolated),
          originalValue: value,
          interpolatedValue: interpolated,
          resolvedValue: props[interpolated],
          entityId: props.id,
          entityCategory: props.category,
          context
        });
        return interpolated;
      }
      const newVisited = new Set(visited);
      newVisited.add(interpolated);
      return resolvePropertyValue(props[interpolated], props, newVisited, depth + 1, context);
    }
    // Resolve again on any change, to handle recursive property values, e.g. "My title is {title}"
    if (RE_INTERPOLATION.test(interpolated)) {
      //return resolvePropertyValue(interpolated, props);
    }
    return interpolated;
  }
  return value;
};
export const interpolateEdgeDetails = (edge, dataset, fromCat, toCat, allDatasets = null) => {
  const details = {description: "", ...edge};
  details.id = getExternalID(details.dataset_id);
  const dsCats = dataset.categories || {};
  const catPrefix = details.dataset_id?.match(/^([a-z][-_a-z]+):(.+)$/);
  if (catPrefix && dsCats[catPrefix[1]]) {
    details.id = catPrefix[2];
  }
  const ds_edge_default_props = dataset.edge_properties || {};
  const ds_edge_props = ds_edge_default_props[`${fromCat}-${toCat}`] || ds_edge_default_props[`${toCat}-${fromCat}`] || {};
  const edge_props = {...ds_edge_default_props, ...ds_edge_props};
  const context = { source: 'interpolateEdgeDetails', datasetId: dataset?.id, fromCat, toCat, edgeId: edge.id };

  details.title = resolvePropertyValue(details.title || edge_props.title,
                                  {...edge_props, ...details}, new Set(), 0, { ...context, property: 'title' }) || "";
  details.description = resolvePropertyValue(details.description || edge_props.description,
                                        {...edge_props, ...details}, new Set(), 0, { ...context, property: 'description' }) || "";
  if (!details.url) {
    const catUrl = catPrefix && dsCats[catPrefix[1]]?.url;
    let resolvedUrl = edge_props.url || catUrl || (dataset && (dataset.edge_url || dataset.url)) || null;
    // If details.id is itself a nested "ds:id" (e.g. "pubmed:22623623" from plex:publication:pubmed:22623623
    // or "pubmed:10508479" from entrezgene:publication:pubmed:10508479), resolve the URL from the nested
    // dataset so it points to the actual source record rather than the parent's category URL.
    if (details.id?.includes(':') && allDatasets) {
      const [nestedDsKey, nestedId] = details.id.split(':', 2);
      const nestedDs = allDatasets[nestedDsKey];
      if (nestedDs) {
        const nestedCatKey = catPrefix?.[1];
        const nestedCatUrl = nestedCatKey && nestedDs.categories?.[nestedCatKey]?.url;
        const betterUrl = nestedCatUrl || nestedDs.edge_url;
        // Use the nested dataset's URL when available — it points to the specific record
        if (betterUrl) {
          resolvedUrl = betterUrl;
          details.id = nestedId;
        }
      }
    }
    details.url = resolvedUrl;
  }
  if (details.url) {
    details.url = resolvePropertyValue(details.url, {positionalArgs: [], ...edge_props, ...details}, new Set(), 0, { ...context, property: 'url' });
  }
  if (details.url) {
    // Hack for bad bindingdb URLs (from downloaded BDB data); newer data uses just a partial path of the URL
    // since the base part of the URL incorrect
    details.url = details.url
      .replace("http://www.bindingdb.org/jsp/dbsearch/", "")
      .replace("www.bindingdb.org/jsp", "www.bindingdb.org/rwd/jsp");
  }
  return details;
};

export const interpolateEntityFields = (
  templates,
  entity,
  dataset = {},
) => {
  const dsCats = dataset.categories;
  const dsCat = (dsCats && dsCats[entity.category]) || {};
  const dsBaseCat = (dsCats && dsCats[entity.baseCategory]) || {};
  const dsCustomProps = {
    ...((dsBaseCat.templates && dsBaseCat.templates[TEMPLATE_CONSTANTS.PROPERTIES]) || {}),
    ...((dsCat.templates && dsCat.templates[TEMPLATE_CONSTANTS.PROPERTIES]) || {}),
  };
  const categoryProps = deriveTemplate(templates, TEMPLATE_CONSTANTS.PROPERTIES, entity.category,
                                       entity.baseCategory, dsCustomProps, {});
  // Dataset-specific properties supersede category-specific properties, while
  // title/description may depend on other definitions.
  // Property "label" may depend on the title, so evaluate it last
  const priorityPropNames = new Set([
    ...Object.keys(categoryProps).filter(x => x.startsWith("_")),
    ...Object.keys(dsCustomProps).filter(x => x.startsWith("_")),
    'title', 'description', 'label',
    ...Object.keys(categoryProps).filter(x => !x.startsWith("_")),
    ...Object.keys(dsCustomProps).filter(x => !x.startsWith("_")),
  ]);
  // Never replace these names
  const immutablePropertyNames = new Set(["id", "category", "dataset", "inchi", "inchikey"]);
  // Populate template properties
  // Template definitions should override existing properties (required when indexed data is incorrect or obsolete)
  const merged = {...entity, ...categoryProps};
  const context = { source: 'interpolateEntityFields', datasetId: dataset?.id, category: entity.category, baseCategory: entity.baseCategory };
  priorityPropNames.forEach(name =>
                              merged[name] = resolvePropertyValue(merged[name], merged, new Set(), 0, { ...context, property: name }));
  Object.keys(merged)
    .filter(p => !new Set(priorityPropNames).has(p))
    .filter(p => !immutablePropertyNames.has(p))
    .forEach(name => merged[name] = resolvePropertyValue(merged[name], merged, new Set(), 0, { ...context, property: name }));
  Object.entries(merged).forEach(([k, v]) => {
    if (typeof(v) === "string") {
      // Clean up any literal HTML br tags from source data before interpolation
      merged[k] = v.replace(/<br[^>]*>/gi, "\n").trim();
    }
  });
  if (entity.title && entity.title.indexOf("(unmapped compound)") !== -1 && !entity.unmapped) {
    const data = {entity: toJS(entity), dataset: toJS(dataset), merged: toJS(merged)};
    console.error(`Incorrectly setting title on non-unmapped entity`, data);
    captureMessage("Incorrectly setting title on non-unmapped entity", "error", data);
  }
  return merged;
};

// Extract balanced content between parentheses, handling nested parens
const extractBalancedParens = (text, startPos) => {
  let depth = 0;
  let start = -1;
  for (let i = startPos; i < text.length; i++) {
    const char = text[i];
    if (char === '(') {
      if (depth === 0) start = i + 1;
      depth++;
    } else if (char === ')') {
      depth--;
      if (depth === 0) {
        return { content: text.substring(start, i), end: i };
      }
    }
  }
  return null;
};

export const interpolateSourceLinks = (mdText, resolveSourceLinks = async (ids) => ids, mentionedGenes = null) => {
  // Replace any standalone "ID" with [ID](ID)
  const linkifiedMD = stylizePublications(linkifyPlexIds(mdText));
  // Replace any "](ID)" with "](SOURCE_LINK "ID")"
  // This handles IDs containing parentheses like SMILES: smiles:C=c1[nH]n(-c2ccccc2)c(=O)...
  const ids = [];

  // Find all markdown links: [text](url)
  // Look for ]( pattern and extract balanced content until closing )
  let pos = 0;
  while (pos < linkifiedMD.length) {
    const linkStart = linkifiedMD.indexOf('](', pos);
    if (linkStart === -1) break;

    const result = extractBalancedParens(linkifiedMD, linkStart + 1);
    if (result) {
      const id = result.content;
      // Skip URLs that are already resolved or use standard URI schemes (mailto:, tel:, ftp:, etc.)
      if (!/^(https?|ftp|ftps|mailto|tel|sms|geo):/.test(id) && !/^\/\//.test(id)) {
        ids.push(id);
      }
      pos = result.end + 1;
    } else {
      pos = linkStart + 2;
    }
  }

  return resolveSourceLinks(ids)
    .then(resolved => {

      let result = linkifiedMD;
      let pos = 0;
      let offset = 0;
      // Snapshot the shared set so repeated calls for the same block don't
      // accumulate — only the caller merges new entries after committing.
      const firstMentions = mentionedGenes ? new Set(mentionedGenes) : new Set();
      // url -> {title, description} of each resolved entity linked, for its source card (PLEX-5920)
      const descriptions = {};

      while (pos < linkifiedMD.length) {
        const linkStart = linkifiedMD.indexOf('](', pos);
        if (linkStart === -1) break;

        const extraction = extractBalancedParens(linkifiedMD, linkStart + 1);
        if (extraction) {
          const id = extraction.content;
          if (!/^(https?|ftp|ftps|mailto|tel|sms|geo):/.test(id) && !/^\/\//.test(id)) {
            if (resolved[id]) {
            // Find the start of the link text [
            let textStart = linkStart;
            while (textStart > 0 && result[textStart + offset] !== '[') {
              textStart--;
            }

            const resolvedData = resolved[id];
            const isFirstMention = !firstMentions.has(id);

            // For genes (targets), use long name on first mention, short name thereafter
            let name;
            if (resolvedData.failed || resolvedData.evidence) {
              // Nothing resolved (e.g. a bdb: measurement id, which isn't an entity): keep the
              // link text as written, such as "IC50 = 631 nM", rather than the bare id (PLEX-6676).
              // An evidence record's ID written bare reads as its dataset's evidence (PLEX-6500).
              const written = linkifiedMD.substring(textStart + 1, linkStart);
              if (resolvedData.evidence && (!written || written === id)) {
                name = resolvedData.name;
              } else {
                name = written || id;
              }
            } else if (resolvedData.category === 'target' && resolvedData.shortName) {
              name = isFirstMention ? resolvedData.name : resolvedData.shortName;
            } else {
              name = resolvedData.name || id;
            }
            // Strip leading [GENE_SYMBOL] bracket notation but keep remaining
            // brackets (e.g. [Homo sapiens]) — they're escaped on line 421 and
            // prepareMarkdown skips already-escaped brackets.
            name = name.replace(/^\[([A-Za-z][A-Za-z0-9-]*)\]\s*/, "$1 ");

            const url = resolvedData.url || id;
            if (!resolvedData.failed && resolvedData.description && !descriptions[url]) {
              descriptions[url] = { title: resolvedData.name || null, description: resolvedData.description };
            }

            // Mark this ID as mentioned
            firstMentions.add(id);

            // Add warning icon for failed resolutions in dev/beta
            const warningIcon = (DEV || BETA) && resolvedData.failed ? ' ⚠️' : '';

            // Replace [old-text](id) with [name](url "id") or [name ⚠️](url "id") if failed
            const before = result.substring(0, textStart + offset);
            // Use extraction.end to find the closing paren position in the original string,
            // then apply offset to find it in the result string
            // This correctly handles IDs followed by other characters (like "BRD4) - text")
            const after = result.substring(extraction.end + offset + 1);
            // Escape brackets in name — nested brackets in link text break markdown parsers
            const safeName = name.replace(/([[\]])/g, "\\$1");
            // No title on a link to several ids ([text](a|b)): prepareMarkdown splits the target at
            // its pipes, title included, and the link stayed raw Markdown (PLEX-6676).
            const title = id.includes('|') ? '' : ` "${id}"`;
            // An evidence record with no URL to build stays as text, not a link that goes nowhere.
            const replacement = resolvedData.evidence && !resolvedData.url
              ? safeName
              : `[${safeName}${warningIcon}](${url}${title})`;

            
            const oldLength = (extraction.end - textStart) + 1;
            result = before + replacement + after;
            offset += replacement.length - oldLength;
            }
          }
          pos = extraction.end + 1;
        } else {
          pos = linkStart + 2;
        }
      }

      return { text: result, mentions: firstMentions, descriptions };
    });
};


export const InterpolatedProperty = ({property = null}) => property;
